import React, { useCallback, useState, ReactNode } from 'react';
import { useQueryParam, StringParam } from 'use-query-params';
import { parse as parseQueryString, stringify } from 'query-string';
import { useLocation } from 'react-router-dom';
import { defaultUrl, ccxUrl, userServiceUrl } from '../services/const';
import { apiPost } from '../services/api';

interface CustomAppContextType {
    currentApp: string;
    setCurrentApp: (app: string) => void;
    getCustomAppPath: (
        path: string | { pathname: string; search: string },
    ) => string | { pathname: string; search: string };
    getCustomAppButtonStyle: () => {
        backgroundColor: string;
        borderColor: string;
    };
    getCustomAppRedirectUrl: () => string;
    getCustomAppStartUrl: ({ promoCode }: { promoCode?: string }) => string;
    getCustomAppUrl: () => string;
    getVerificationEmailPath: () => string;
    getCurrentAppFullName: () => string;
    adminLogin: ({
        login,
        password,
    }: {
        login: string;
        password: string;
    }) => Promise<any>;
    getCustomAppAdminRedirectUrl: () => string;
}

interface CustomAppProviderProps {
    children: ReactNode;
    app?: string;
}

export const CustomAppContext = React.createContext<CustomAppContextType>(
    {} as CustomAppContextType,
);

export const APP_LABS = 'labs';
export const APP_CCX = 'ccx';
export const APP_CCX_FULLNAME = 'CCX';
export const USER_AUTH_URL =
    userServiceUrl || 'https://ccx.s9s-dev.net/api/auth';
export const CCX_ADMIN_REDIRECT_PATH =
    window?.FEAS_ENV?.REACT_APP_CCX_ADMIN_REDIRECT_PATH ||
    'https://ccx.s9s-dev.net/admin';

export const CustomAppProvider = ({
    children,
    app = APP_CCX,
}: CustomAppProviderProps) => {
    const [fromApp] = useQueryParam('from', StringParam);
    const location = useLocation();

    const [currentApp, setCurrentApp] = useState(
        fromApp || window?.FEAS_ENV?.REACT_APP_CURRENT_APP || app,
    );
    const searchObj = window?.FEAS_ENV?.REACT_APP_CURRENT_APP
        ? { from: window.FEAS_ENV.REACT_APP_CURRENT_APP }
        : parseQueryString(location.search) || {};

    async function adminLogin({
        login,
        password,
    }: {
        login: string;
        password: string;
    }) {
        const res = await apiPost(`${USER_AUTH_URL}/admin-login`, {
            login,
            password,
        });
        return res;
    }

    const getCurrentAppFullName = useCallback(
        () => {
            return APP_CCX_FULLNAME;
        },

        // eslint-disable-next-line react-hooks/exhaustive-deps
        [searchObj],
    );

    const getCustomAppPath = useCallback(
        (path: string | { pathname: string; search: string }) => {
            if (searchObj.from) {
                if (typeof path === 'string') {
                    return { pathname: path, search: `?from=${currentApp}` };
                } else {
                    const pathSearchObj = parseQueryString(path.search);
                    return {
                        ...path,
                        search: stringify({
                            ...pathSearchObj,
                            from: currentApp,
                        }),
                    };
                }
            } else {
                return path;
            }
        },

        // eslint-disable-next-line react-hooks/exhaustive-deps
        [searchObj],
    );

    const getCustomAppAdminRedirectUrl = () => {
        return CCX_ADMIN_REDIRECT_PATH || `${defaultUrl}/dashboard?from=login`;
    };

    const getCustomAppRedirectUrl = useCallback(
        () => {
            return ccxUrl;
        },

        // eslint-disable-next-line react-hooks/exhaustive-deps
        [searchObj],
    );

    const getVerificationEmailPath = useCallback(
        () => {
            return '/api/auth/resend-verification-email';
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [searchObj],
    );

    const getCustomAppUrl = useCallback(
        () => {
            return ccxUrl;
        },

        // eslint-disable-next-line react-hooks/exhaustive-deps
        [searchObj],
    );

    const getCustomAppStartUrl = useCallback(
        ({ promoCode }: { promoCode?: string }) => {
            return `${ccxUrl}${promoCode ? `?pcode=${promoCode}` : ''}`;
        },

        // eslint-disable-next-line react-hooks/exhaustive-deps
        [searchObj],
    );

    /**
     * function for getting button styles depending on the app
     * @type {Function}
     */
    const getCustomAppButtonStyle = useCallback(() => {
        return { backgroundColor: '#160482', borderColor: '#160482' };
    }, [searchObj]);
    const customAppContext: CustomAppContextType = {
        currentApp,
        setCurrentApp,
        getCustomAppPath,
        getCustomAppButtonStyle,
        getCustomAppRedirectUrl,
        getCustomAppStartUrl,
        getCustomAppUrl,
        getVerificationEmailPath,
        getCurrentAppFullName,
        adminLogin,
        getCustomAppAdminRedirectUrl,
    };
    return (
        <CustomAppContext.Provider value={customAppContext}>
            {children}
        </CustomAppContext.Provider>
    );
};
